import { forwardRef } from 'react';
import { Card, CardContent, CardMedia, Text } from '@sixui/ui';
import { cardTokens } from '@sixui/ui/components/Card/Card.stylex';
import { cardContentTokens } from '@sixui/ui/components/CardContent/CardContent.stylex';
import { colorSchemeTokens } from '@sixui/ui/themes/base/colorScheme.stylex';
import { shapeTokens } from '@sixui/ui/themes/base/shape.stylex';
import stylex from '@stylexjs/stylex';

import type { IContainerProps } from '~/components/utils/Container';
import type { IAny, IMaybeAsync } from '~/helpers/types';
import { ContentBlock } from '../ContentBlock';

export type IHorizontalCardSize = 'sm' | 'md';

export type IHorizontalCardProps = IContainerProps & {
  size?: IHorizontalCardSize;
  image?: string;
  color?: string;
  icon?: React.ReactNode;
  href?: string;
  onClick?: (event?: React.MouseEvent<HTMLElement>) => IMaybeAsync<IAny>;
  title?: React.ReactNode;
  primary?: boolean;
  placeholder?: boolean;
  disabled?: boolean;
  dragged?: boolean;
  children?: React.ReactNode;
  draft?: boolean;
};

const styles = stylex.create({
  host: {
    borderRadius: shapeTokens.corner$md,
    backgroundColor: 'transparent',
  },
  card: {
    flexDirection: 'row',
    [cardTokens.containerShape]: shapeTokens.corner$md,
  },
  card$primary: {
    [cardTokens.containerColor]: colorSchemeTokens.secondaryContainer,
  },
  card$placeholder: {
    [cardTokens.containerColor]: 'transparent',
  },
  media: {
    borderRadius: shapeTokens.corner$md,
    flexShrink: 0,
    backgroundColor: colorSchemeTokens.surfaceDim,
    color: colorSchemeTokens.onSurface,
  },
  media$placeholder: {
    backgroundColor: colorSchemeTokens.surfaceContainerHighest,
  },
  media$sm: {
    minWidth: 54,
    minHeight: 54,
  },
  media$md: {
    minWidth: 72,
    minHeight: 72,
  },
  media$color: (color: string) => ({
    backgroundColor: color,
  }),
  icon$sm: {
    fontSize: 20,
  },
  icon$md: {
    fontSize: 24,
  },
  content: {
    display: 'flex',
    flexDirection: 'row',
    flexGrow: 1,
    alignItems: 'center',
    color: colorSchemeTokens.onSurface,
    gap: 6,

    [cardContentTokens.topSpace]: 6,
    [cardContentTokens.bottomSpace]: 6,
    [cardContentTokens.topSpace$actionable]: 6,
    [cardContentTokens.bottomSpace$actionable]: 6,

    [cardContentTokens.leadingSpace]: 8,
    [cardContentTokens.trailingSpace]: 8,
    [cardContentTokens.leadingSpace$actionable]: 8,
    [cardContentTokens.trailingSpace$actionable]: 8,
  },
  content$sm: {},
  content$md: {
    [cardContentTokens.leadingSpace]: 12,
    [cardContentTokens.trailingSpace]: 12,
    [cardContentTokens.leadingSpace$actionable]: 12,
    [cardContentTokens.trailingSpace$actionable]: 12,
  },
  content$placeholder: {
    color: colorSchemeTokens.onSurfaceVariant,
  },
  title: {
    flexGrow: 1,
    minWidth: '0',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    hyphens: 'auto',
  },
  title$sm: {
    WebkitLineClamp: 2,
  },
  title$md: {
    WebkitLineClamp: 3,
  },
});

const cardPlaceholderStyles = stylex.create({
  outline: {
    borderStyle: 'dashed',
  },
});

export const HorizontalCard = forwardRef(function HorizontalCard(
  props: IHorizontalCardProps,
  ref?: React.Ref<HTMLDivElement>,
) {
  const {
    sx,
    size = 'md',
    image,
    color,
    icon,
    href,
    onClick,
    primary,
    placeholder,
    title,
    disabled,
    dragged,
    children,
    draft,
    ...other
  } = props;

  return (
    <ContentBlock flat draft={draft} draftOutlineSize={size} sx={styles.host}>
      <Card
        ref={ref}
        variant={placeholder ? 'outlined' : 'filled'}
        styles={placeholder ? cardPlaceholderStyles : undefined}
        sx={[
          styles.card,
          primary && styles.card$primary,
          placeholder && styles.card$placeholder,
          sx,
        ]}
        href={dragged ? undefined : href}
        onClick={onClick}
        disabled={disabled}
        visualState={{ dragged }}
        {...other}
      >
        <CardMedia
          sx={[
            styles.media,
            placeholder && styles.media$placeholder,
            styles[`media$${size}`],
            color ? styles.media$color(color) : null,
          ]}
          src={image}
        >
          {icon ? (
            <div {...stylex.props(styles[`icon$${size}`])}>{icon}</div>
          ) : null}
        </CardMedia>
        <CardContent
          sx={[
            styles.content,
            styles[`content$${size}`],
            placeholder && styles.content$placeholder,
          ]}
        >
          <Text
            variant="title"
            size="sm"
            sx={[styles.title, styles[`title$${size}`]]}
          >
            {title}
          </Text>
        </CardContent>
        {children}
      </Card>
    </ContentBlock>
  );
});
